import { useQuery } from '@tanstack/react-query'
import ky from 'ky'

import { extractHttpError } from '../../../components/api/extractHttpError'
import { API_PREFIX } from '../../../config'
import { logEvents } from '../../logEvents'

export type VoiceBotHandoverResponse = {
  uid: string
  pccSessionId: string
  created: string
  summary: string
}

export const useVoiceBotHandover = (
  copilotToken: string | null,
  sessionId: string,
) =>
  useQuery<VoiceBotHandoverResponse | null>({
    enabled: !!(copilotToken && sessionId),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryKey: ['voicebot-handover', copilotToken, sessionId],
    // A 204 means botstudio has not delivered the summary yet, so poll until it
    // does. Capped at 60 polls. Keeps polling with the tab backgrounded — the
    // call can arrive while the agent is in another tab.
    refetchIntervalInBackground: true,
    refetchInterval: ({ state }) => {
      if (state.status === 'error' || state.dataUpdateCount > 60) return false
      return state.data == null ? 1000 : false
    },
    queryFn: async ({ signal }) => {
      try {
        const response = await ky.get(
          `${API_PREFIX}/co-pilot/v1/voicebot-handover/by-session-id`,
          {
            signal,
            searchParams: { sessionId, token: copilotToken || '' },
          },
        )

        // Nothing yet — refetchInterval polls until the summary arrives.
        if (response.status === 204) return null

        return await response.json<VoiceBotHandoverResponse>()
      } catch (error) {
        // React Query cancels this on unmount; that is not a failure
        if (signal.aborted) throw error

        const e = await extractHttpError(error)
        logEvents({
          eventType: 'useVoiceBotHandover error',
          level: 'error',
          name: e.name,
          message: e.message,
          ...e.cause,
        })

        throw e
      }
    },
  })
